import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Music, Trash2, CheckCircle2, XCircle, FolderOpen, ChevronDown, ChevronUp } from "lucide-react";
import { PriceToggle } from "@/components/PriceToggle";
import { TagsInput, tagsToForm } from "@/components/TagsInput";
import { HighlightSelect, sectionsToForm } from "@/components/HighlightSelect";

const inputCls = "w-full bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-white text-sm";

const splitName = (name) => {
  const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(name);
  return m ? { artist: m[1].trim(), title: m[2].trim() } : { artist: "", title: name };
};

const detectCategory = (name) => (/mashup/i.test(name) ? "mashup" : /remix/i.test(name) ? "remix" : "pool");

const fromFile = (file) => ({
  key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  ...splitName(file.name.replace(/\.[^.]+$/, "")),
  category: detectCategory(file.name),
  price: "0.99",
  genre: "",
  bpm: "",
  tags: [],
  is_featured: false,
  is_top: false,
  is_highlighted: false,
  is_new: false,
  sections: [],
  versions: [],
  status: "idle",
  progress: 0,
});

export function BulkUpload({ onDone }) {
  const [rows, setRows] = useState([]);
  const [over, setOver] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const audio = Array.from(list).filter((f) => f.type.startsWith("audio/") || /\.(mp3|wav|flac|aiff?|m4a|ogg)$/i.test(f.name));
    if (!audio.length) return toast.error("Solo archivos de audio");
    setRows((r) => [...r, ...audio.map(fromFile)]);
  };

  const patch = (key, data) => setRows((r) => r.map((x) => (x.key === key ? { ...x, ...data } : x)));
  const remove = (key) => setRows((r) => r.filter((x) => x.key !== key));

  const uploadAll = async () => {
    const pending = rows.filter((r) => r.status !== "done");
    if (!pending.length) return;
    if (pending.some((r) => !r.title.trim())) return toast.error("Todos los tracks necesitan título");
    setRunning(true);
    let ok = 0;
    for (const row of pending) {
      patch(row.key, { status: "uploading", progress: 0 });
      const fd = new FormData();
      fd.append("title", row.title.trim());
      fd.append("artist", row.artist || "");
      fd.append("category", row.category);
      fd.append("price", row.price || "0");
      fd.append("genre", row.genre || "");
      if (row.bpm) fd.append("bpm", row.bpm);
      fd.append("tags", tagsToForm(row.tags));
      fd.append("is_featured", row.is_featured ? "true" : "false");
      fd.append("is_top", row.is_top ? "true" : "false");
      fd.append("is_highlighted", row.is_highlighted ? "true" : "false");
      fd.append("is_new", row.is_new ? "true" : "false");
      fd.append("sections", sectionsToForm(row.sections));
      (row.versions || []).forEach((v) => fd.append("versions", v));
      if (row.versions?.length) fd.append("version_meta", JSON.stringify(row.versions.map((v) => { const base = v.name.replace(/\.[^.]+$/, ""); const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(base); return { title: m ? m[2].trim() : base, artist: m ? m[1].trim() : row.artist, price: row.price || "0" }; })));
      fd.append("audio", row.file);
      try {
        await api.post("/admin/tracks", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => ev.total && patch(row.key, { progress: Math.round((ev.loaded / ev.total) * 100) }),
        });
        patch(row.key, { status: "done", progress: 100 });
        ok++;
      } catch (err) {
        console.error(err);
        patch(row.key, { status: "error" });
      }
    }
    setRunning(false);
    toast[ok === pending.length ? "success" : "warning"](`${ok}/${pending.length} tracks subidos`);
    onDone();
  };

  const pendingCount = rows.filter((r) => r.status !== "done").length;

  return (
    <div className="space-y-4" data-testid="bulk-upload">
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="bulk-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          over ? "border-red-500 bg-red-950/20" : "border-white/10 bg-[#111116] hover:border-white/25"
        }`}
      >
        <input ref={inputRef} type="file" multiple accept="audio/*" className="hidden" data-testid="bulk-file-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <FolderOpen className="w-8 h-8 mx-auto text-red-500 mb-3" />
        <div className="font-display font-bold text-lg">Arrastra tus MP3/WAV aquí</div>
        <div className="text-sm text-white/40 mt-1">o haz clic para seleccionar varios archivos · los títulos con "Remix" o "Mashup" van a su sección, el resto a Pool</div>
      </div>

      {rows.length > 0 && (
        <>
          <div className="hidden md:grid grid-cols-[1fr_130px_110px_260px_40px_40px] gap-2 px-3 font-mono text-[10px] uppercase tracking-widest text-white/40">
            <span>Título</span><span>Artista</span><span>Categoría</span><span>Precio €</span><span /><span />
          </div>
          <div className="space-y-2">
            {rows.map((r) => <Row key={r.key} row={r} onPatch={patch} onRemove={remove} disabled={running} />)}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button type="button" onClick={() => setRows((x) => x.filter((r) => r.status !== "done"))} disabled={running}
              data-testid="bulk-clear-done" className="text-xs text-white/50 hover:text-white">
              Limpiar completados
            </button>
            <button type="button" onClick={uploadAll} disabled={running || pendingCount === 0} data-testid="bulk-upload-all"
              className="btn-red text-white px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {running ? "Subiendo..." : `Subir ${pendingCount} track${pendingCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ row, onPatch, onRemove, disabled }) {
  const locked = disabled || row.status === "done";
  const [open, setOpen] = useState(false);
  const extras = [row.genre, row.bpm && `${row.bpm} BPM`, ...(row.tags || []).filter(Boolean), row.versions?.length && `${row.versions.length} versiones`, row.is_highlighted && "Destacada", row.is_new && "Novedad", row.is_featured && "Home", row.is_top && "Top", ...(row.sections || [])].filter(Boolean);
  return (
    <div data-testid={`bulk-row-${row.key}`} className="bg-[#111116] border border-white/5 rounded-xl p-3 relative overflow-hidden">
      {row.status === "uploading" && (
        <div className="absolute left-0 bottom-0 h-0.5 bg-red-600 transition-[width]" style={{ width: `${row.progress}%` }} />
      )}
      <div className="grid md:grid-cols-[1fr_130px_110px_260px_40px_40px] gap-2 items-center">
        <div className="flex items-center gap-2 min-w-0">
          <Music className="w-4 h-4 text-red-500 shrink-0" />
          <input className={inputCls} value={row.title} disabled={locked} data-testid="bulk-row-title"
            onChange={(e) => onPatch(row.key, { title: e.target.value })} />
        </div>
        <input className={inputCls} value={row.artist} disabled={locked} data-testid="bulk-row-artist"
          onChange={(e) => onPatch(row.key, { artist: e.target.value })} />
        <select className={inputCls} value={row.category} disabled={locked} data-testid="bulk-row-category"
          onChange={(e) => onPatch(row.key, { category: e.target.value })}>
          <option value="remix">Remix</option>
          <option value="mashup">Mashup</option>
          <option value="original">Original</option>
          <option value="pool">Pool</option>
        </select>
        <div data-testid="bulk-row-price" className={locked ? "pointer-events-none opacity-60" : ""}>
          <PriceToggle small value={row.price} onChange={(price) => onPatch(row.key, { price })} testid={`bulk-row-price-${row.key}`} />
        </div>
        <div className="flex justify-center" data-testid="bulk-row-status">
          {row.status === "done" && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          {row.status === "error" && <XCircle className="w-5 h-5 text-red-500" />}
          {row.status === "uploading" && <span className="font-mono text-xs text-white/60">{row.progress}%</span>}
        </div>
        <button type="button" onClick={() => onRemove(row.key)} disabled={disabled} data-testid="bulk-row-remove"
          className="text-white/40 hover:text-red-500 p-1.5 rounded-lg justify-self-center"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-3 mt-1 pl-6">
        <div className="text-[11px] text-white/30 truncate flex-1">{row.file.name} · {(row.file.size / 1048576).toFixed(1)} MB{extras.length > 0 && <span className="text-white/50"> · {extras.join(" · ")}</span>}</div>
        <button type="button" onClick={() => setOpen(!open)} data-testid={`bulk-row-options-toggle-${row.key}`}
          className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-red-400 flex items-center gap-1 shrink-0">
          Opciones {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      {open && (
        <div className="mt-2 ml-6 p-3 rounded-lg border border-white/10 bg-black/30 space-y-2" data-testid={`bulk-row-options-${row.key}`}>
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Género(s), separados por coma" value={row.genre} disabled={locked} data-testid={`bulk-row-genre-${row.key}`}
              onChange={(e) => onPatch(row.key, { genre: e.target.value })} />
            <input className={inputCls} type="number" placeholder="BPM" value={row.bpm} disabled={locked} data-testid={`bulk-row-bpm-${row.key}`}
              onChange={(e) => onPatch(row.key, { bpm: e.target.value })} />
          </div>
          <TagsInput value={row.tags} onChange={(tags) => onPatch(row.key, { tags })} testid={`bulk-row-tag-${row.key}`} />
          <label className={`flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2 text-xs cursor-pointer ${row.versions?.length ? "border-emerald-700/50 text-emerald-400 bg-emerald-950/10" : "border-white/15 text-white/50 hover:border-white/30"} ${locked ? "pointer-events-none opacity-60" : ""}`} data-testid={`bulk-row-versions-${row.key}`}>
            <span>{row.versions?.length ? `${row.versions.length} versión${row.versions.length === 1 ? "" : "es"} extra: ${row.versions.map((v) => v.name.replace(/\.[^.]+$/, "")).join(", ")}` : "Versiones extra (Extended, Radio Edit…) — clic para añadir"}</span>
            <input type="file" multiple accept="audio/*" className="hidden" disabled={locked} data-testid={`bulk-row-versions-input-${row.key}`}
              onChange={(e) => { onPatch(row.key, { versions: Array.from(e.target.files) }); e.target.value = ""; }} />
          </label>
          <div className={locked ? "pointer-events-none opacity-60" : ""}>
            <HighlightSelect small value={{ is_featured: row.is_featured, is_top: row.is_top, is_highlighted: row.is_highlighted, is_new: row.is_new, sections: row.sections }} category={row.category} onChange={(v) => onPatch(row.key, v)} testid={`bulk-row-highlight-${row.key}`} />
          </div>
        </div>
      )}
    </div>
  );
}
